import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Container, CssBaseline, IconButton, InputAdornment, TextField, Typography } from "@material-ui/core";
import { AccountCircle, LockOutlined, Visibility, VisibilityOff } from '@material-ui/icons';
import Lottie from "react-lottie";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ForgotPasswordRedirector from "../../components/ForgotPasswordAnchor";
import LoginLottie from "../../assets/lottie/login.json";
import WhatsappButton from "../../components/WhatsappButton";

import iconLight from "../../assets/miscPng/icon.png";
import iconDark from "../../assets/miscPng/icon-dark.png";

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import LottieUtils from "../../utils/classes/LottieUtils";

const useStyles = makeStyles((theme) => ({
  //  ****************
  //  ** Containers **
  //  ****************
  loginContainer: {
    width: "100vw",
    height: "90vh",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    marginTop: "5vh",
    userSelect: "none",
    name: theme.name,
    color: theme.palette.lottie.plainBackground,
  },

  imgContainer: {
    backgroundColor: theme.palette.lottie.plainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px 0px 0px 10px",
    [theme.breakpoints.down("sm")]: { display: "none", },
  },

  loginForm: {
    backgroundColor: theme.palette.lottie.secondaryPlainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0px 10px 10px 0px",
  },

  //  ********************
  //  ** Form Container **
  //  ********************
  avatar: {
    margin: theme.spacing(0),
    width: "100px",
    height: "auto",
  },

  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  textField: {
    width: "80%",

    "& label.MuiFormLabel-root": { color: theme.palette.lottie.plainBackground, },

    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: theme.palette.lottie.plainBackground, },
      "&:hover fieldset": { borderColor: theme.palette.lottie.plainBackground, },
      "&.Mui-focused fieldset": { borderColor: "red", },
    },
  },

  textFieldInput: { color: theme.palette.lottie.plainBackground, },

  iconButton: { color: theme.palette.text.primary, },

  floatingButton: {
    margin: theme.spacing(3, 0, 2),
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  secondaryButton: { color: theme.palette.lottie.plainBackground, },

  //  *********
  //  ** Box **
  //  *********
  fxLogo: {
    margin: theme.spacing(1),
    width: "50px",
    height: "auto",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
  },
}));

const Login = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const { handleLogin } = useContext(AuthContext);
  
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const themeValue = localStorage.getItem("theme");
  const currentIcon =  themeValue === "dark" ? iconDark : iconLight;
  user.email = api.defaults.headers.email;

  

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => { document.title = "BestZap"; }, [])

  useEffect(() => {
   if (!user.email) handleBack();
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleBack = () => {
    window.location.href = "https://bestzap.fxsistemas.com.br";
    history.push(DefaultValuesUtils.ROUTES[102]);
  };

  const handleChangeInput = (event) => { setUser({ ...user, [event.target.name]: event.target.value }); };

  const handleSubmit = (event) => { event.preventDefault(); handleLogin(user); };



  //  ************
  //  ** Return **
  //  ************
  return (
    <Container className={classes.loginContainer} component="main">
      <CssBaseline />

      <div className={classes.imgContainer}>
        <Lottie
          options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: LoginLottie }}
          isClickToPauseDisabled={true}
          style={{ width: "80.00%", height: "80.00%", }}
        />
      </div>

      <div className={classes.loginForm}>
        <img className={classes.avatar} src={currentIcon} alt="BestZap Logo" />

        <Typography component="h1" variant="h3">
          {i18n.t("login.title")}
        </Typography>

        <Typography component="h2" variant="h6">
          <br />
          {i18n.t("login.subtitlePassword")}
          <br /><br />
        </Typography>

        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          <TextField
            className={classes.textField}
            label={i18n.t("passwordReset.form.email")}
            autoComplete="on"
            variant="outlined"
            margin="normal"
            id="email"
            name="email"
            type="email"
            value={user.email}
            onChange={handleChangeInput}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><AccountCircle /></InputAdornment>),
              className: classes.textFieldInput
            }}

            required
          />

          <TextField
            className={classes.textField}
            variant="outlined"
            margin="normal"
            style={{width: "80%"}}
            name="password"
            label={i18n.t("login.form.password")}
            id="password"
            value={user.password}
            onChange={handleChangeInput}
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}

            InputProps={{
              className: classes.textFieldInput,
              startAdornment: (<InputAdornment position="start"><LockOutlined /></InputAdornment>),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => setShowPassword((e) => !e)}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}

            autoFocus
            required
          />

          <Button
            type="submit"
            style={{ width: "80%" }}
            variant="contained"
            color="primary"
            className={classes.floatingButton}
          >
            {i18n.t("login.buttons.submit")}
          </Button>

          <ForgotPasswordRedirector />

          <Button
            type="button"
            style={{width: "80%"}}
            variant="outlined"
            color="primary"
            className={`${classes.submit} ${classes.floatingButton}`}  
            onClick={handleBack}
          >
            {i18n.t("login.buttons.back")}
          </Button>
        </form>
      </div>

      <WhatsappButton />
    </Container>
  );
};

export default Login;
